import React, { useState, useEffect, useRef } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';

import { useAccess, useForm, useLista, useManagedContext, useNav, useUnsavedChangesGuard, useFormErrors } from '../../components/hooks';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, dataTaggerActionClear, dataTaggerActionSet, sequenceActionNext } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import { ServerRequest } from '../../utils/apiweb';

import { FormError, Loading, InputEntidad, InputLista, DatePickerCustom, SectionHeading, FormLabel, Select } from '../../components/common';
import { getDateId, getDateNow } from '../../utils/convert';
import { CloneObject, GetMeses, CompareDates } from '../../utils/helpers';
import DireccionForm, {GetDataDireccion, IsValidDireccion} from '../../components/controls/DireccionForm';
import DataTaggerFormRedux from '../../components/controls/DataTaggerFormRedux';
import DocumentosGrid from '../../components/controls/DocumentosGrid';
import ContactosGrid from '../../components/controls/ContactosGrid';
import MediosPagoGrid from '../../components/controls/MediosPagoGrid';
import RubrosAfipGrid from '../../components/controls/RubrosAfipGrid';
import ArcaLookup from '../../components/controls/ArcaLookup';
import ArcaActualizacion from '../../components/controls/ArcaActualizacion';
import useArcaIntegration, { mapArcaDomicilio } from '../../hooks/useArcaIntegration';
import useArcaActualizacion, { identificacionArca, documentosArca, domicilioArcaCompleto } from '../../hooks/useArcaActualizacion';
import { TIPO_PERSONA, TIPO_DOCUMENTO } from '../../consts/entityType';

import { isNull } from '../../utils/validator';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { useMemo } from 'react';
import { TIPS } from '../../consts/tips';

const ARCA_FORMA_JURIDICA_NOMBRES = {
    'SOC. ANONIMA': 'S.A.',
    'SOC. ANONIMA UNIPERSONAL': 'S.A.',
    'SOC. DE RESP. LIMITADA': 'S.R.L.',
    'SOC. ACC. SIMPLIFICADA': 'S.A.S',
    'ASOC. CIVIL': 'Asociación Civil',
    'COOPERATIVA': 'Societaria Cooperativa',
    'FIDEICOMISO': 'Fideicomiso',
    'SOC. DE HECHO': 'Sociedad de Hecho',
    'SOC. COLECTIVA': 'Otras',
    'SOC. EN COMANDITA SIMPLE': 'Otras',
    'SOC. DE CAPITAL E INDUSTRIA': 'Otras',
    'FUNDACION': 'Otras',
    'MUTUAL': 'Otras',
    'ORGANISMO PUBLICO': 'Otras',
};

function mapFormaJuridica(arcaFormaJuridica, listaFormaJuridica) {
    if (!arcaFormaJuridica) return 0;
    const nombreLocal = ARCA_FORMA_JURIDICA_NOMBRES[arcaFormaJuridica];
    if (!nombreLocal || !listaFormaJuridica || listaFormaJuridica.length === 0) return 0;
    const normalize = s => s.trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const item = listaFormaJuridica.find(f => normalize(f.nombre) === normalize(nombreLocal));
    return item ? item.id : 0;
}

function PersonaJuridicaView() {
     
    //parametros url
    const params = useParams();
    const navigate = useNav()
    const { showMessageModal } = useManagedContext()

    //variables
    const entityInit = {
        persona: {
            id: 0,
            idTipoDocumento: 0,
            numeroDocumento: '',
            denominacion: '',
            nombreFantasia: '',
            idFormaJuridica: 0,
            idJurisdiccion: 0,
            fechaConstitucion: null,
            mesCierre: 0,
            // el alta con ARCA lo pone en 'ARCA' (useArcaIntegration), como en la física
            origen: ''
        },
        domicilioLegal: {
            id: 0,
            entidad: "PersonaJuridicaDomicilioLegal",
            idEntidad: 0,
            idTipoGeoreferencia: 0,
            idPais: 0,
            idProvincia: 0,
            idLocalidad: 0,
            idZonaGeoreferencia: 0,
            codigoPostal: "",
            calle: "",
            entreCalle1: "",
            entreCalle2: "",
            altura: "",
            piso: "",
            dpto: "",
            referencia: "",
            longitud: 0,
            latitud: 0,
            origen: ""
        },
        domicilioFiscal: {
            id: 0,
            entidad: "PersonaJuridicaDomicilioFiscal",
            idEntidad: 0,
            idTipoGeoreferencia: 0,
            idPais: 0,
            idProvincia: 0,
            idLocalidad: 0,
            idZonaGeoreferencia: 0,
            codigoPostal: "",
            calle: "",
            entreCalle1: "",
            entreCalle2: "",
            altura: "",
            piso: "",
            dpto: "",
            referencia: "",
            longitud: 0,
            latitud: 0,
            origen: ""
        },
        archivos: [],
        observaciones: [],
        etiquetas: [],
        documentos: [],
        contactos: [],
        mediosPago: [],
        rubrosAfip: []
    };     

    //hooks
    const [state, setState] = useState({
        id: params.id ? parseInt(params.id) : 0,
        mode: params.mode,
        loading: false,
        entity: entityInit,
        showInfo: false,    
        accordions: {
            datos: true,
            documentos: false,
            contactos: false,
            domicilioLegal: false,
            domicilioFiscal: false,
            mediosPago: false,
            rubrosAfip: false
        },
        showMenu: false
    });

    const [pendingChange, setPendingChange] = useState(false);

    const [processKey, setProcessKey] = useState(null);

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    const mount = () => {

        const _processKey = `PersonaJuridica_${state.id??0}_${getDateId()}`;
        setProcessKey(_processKey);

        if (state.id > 0) {
            FindPersonaJuridica();
        }
        else {
            dispatch(dataTaggerActionSet(_processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }));
        }

        const unmount = () => {}
        return unmount;
    }
    useEffect(mount, []);

    const [accessMedioPago, setAccessMedioPago] = useState({view: false, edit: false});
    const [access, readyAceess] = useAccess({
        key: '_SecurityAccesses',
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        }
    });
    useEffect(() => {
        if (readyAceess) {
            const medio_pago_view = access.some(f => f.codigo === 'medio-pago_view');
            const medio_pago_edit = access.some(f => f.codigo === 'medio-pago_edit');
            setAccessMedioPago({view: medio_pago_view, edit: medio_pago_edit});
        }
    }, [readyAceess]);

    useEffect(() => {
        if (processKey && state.entity.persona.id > 0) {
            dispatch(dataTaggerActionSet(processKey, {
                Archivo: state.entity.archivos,
                Observacion: state.entity.observaciones,
                Etiqueta: state.entity.etiquetas
            }));        
        }
    }, [processKey, state.entity]);

    const formInitialValues = {
		idTipoDocumento: 0,
		numeroDocumento: '',
		denominacion: '',
		nombreFantasia: '',
		idFormaJuridica: 0,
		idJurisdiccion: 0,
		fechaConstitucion: null,
		mesCierre: 0
    };
    const [ formValues, formHandle, , formSet ] = useForm(formInitialValues);
    // ref a formValues actual: usado por callbacks async para no pisar ediciones del usuario
    const formValuesRef = useRef(formValues);
    useEffect(() => { formValuesRef.current = formValues; }, [formValues]);

    const arca = useArcaIntegration({
        isNew: state.mode === OPERATION_MODE.NEW,
        tipoPersonaEsperado: 'JURIDICA',
        mapearFormValues: (persona) => ({
            ...(persona.razonSocial && { denominacion: persona.razonSocial }),
            ...(persona.mesCierre && { mesCierre: persona.mesCierre }),
            ...(persona.fechaContratoSocial && { fechaConstitucion: new Date(persona.fechaContratoSocial + 'T12:00:00') }),
        }),
        mapearDomicilios: (persona, entity) => {
            if (persona.domicilioFiscal) entity.domicilioFiscal = { ...entity.domicilioFiscal, ...mapArcaDomicilio(persona.domicilioFiscal) };
            if (persona.domicilioLegal) entity.domicilioLegal = { ...entity.domicilioLegal, ...mapArcaDomicilio(persona.domicilioLegal) };
            return entity;
        },
        camposArca: (data) => ({
            denominacion: !!data.razonSocial,
            idFormaJuridica: !!data.formaJuridica && !!ARCA_FORMA_JURIDICA_NOMBRES[data.formaJuridica],
            mesCierre: !!data.mesCierre,
            fechaConstitucion: !!data.fechaContratoSocial,
        }),
        formValues, formSet, setState, setPendingChange,
        onPostProcess: (persona) => {
            if (persona.formaJuridica) {
                ServerRequest(
                    REQUEST_METHOD.GET, null, true, APIS.URLS.LISTA, '/FormaJuridica', null,
                    (response) => {
                        response.json().then(data => {
                            const lista = data.FormaJuridica || [];
                            const id = mapFormaJuridica(persona.formaJuridica, lista);
                            // mergear con formValues actuales (no snapshot) para no pisar ediciones
                            if (id > 0) formSet({ ...formValuesRef.current, idFormaJuridica: id });
                        });
                    }, null, null
                );
            }
        },
    });

    const arcaOrigen = state.entity.persona.origen === 'ARCA';
    // El documento del encabezado: hay jurídicas con el CUIT sólo ahí, sin una fila en la grilla
    const cabecera = { idTipoDocumento: formValues.idTipoDocumento, numeroDocumento: formValues.numeroDocumento };
    const actualizacionArca = useArcaActualizacion({
        persona: state.entity.persona,
        disponible: state.mode === OPERATION_MODE.EDIT && state.entity.persona.id > 0,
        domicilios: ['domicilioLegal', 'domicilioFiscal'],
    });
    // domicilios de ARCA aplicados en la edición: cada formulario se vuelve a montar en cada aplicación para georreferenciarlo
    const domicilioLegalArca = actualizacionArca.domicilioAplicado('domicilioLegal');
    const domicilioFiscalArca = actualizacionArca.domicilioAplicado('domicilioFiscal');

    const dispatch = useDispatch();
    const dataTagger = useSelector( (state) => state.dataTagger.data );
    const sequenceValue = useSelector( (state) => state.sequence.value );
    
    const [getListLista, ] = useLista({
        listas: ['TipoGeoreferencia', 'FormaJuridica'],
        onLoaded: (listas, isSuccess, error) => {
          if (isSuccess) {
            const listTipoGeoreferencia = getListLista('TipoGeoreferencia');
            const idTipoGeoreferencia = (listTipoGeoreferencia.length > 0) ? listTipoGeoreferencia[0].id : 530; //el primero u Open Street Map
            setState(prevState => {
                let data = CloneObject(prevState.entity);
                data.domicilioLegal.idTipoGeoreferencia = idTipoGeoreferencia;
                data.domicilioFiscal.idTipoGeoreferencia = idTipoGeoreferencia;
                return {...prevState, entity: data};
            });
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoGeoreferencia',
          timeout: 0
        }
    });

    const isNoHeader = useMemo(() => window.location.pathname.split('/')[2] === 'nh', [window.location.pathname])

    const arcaAutoGeocodeLegal = useMemo(() => {
        const dom = arca.arcaData?.domicilioLegal || domicilioLegalArca?.domicilio;
        return dom ? { country: 'ARGENTINA', state: dom.descripcionProvincia, city: dom.localidad, street: dom.calle, number: dom.numero } : null;
    }, [arca.arcaData, domicilioLegalArca]);
    const arcaAutoGeocodeFiscal = useMemo(() => {
        const dom = arca.arcaData?.domicilioFiscal || domicilioFiscalArca?.domicilio;
        return dom ? { country: 'ARGENTINA', state: dom.descripcionProvincia, city: dom.localidad, street: dom.calle, number: dom.numero } : null;
    }, [arca.arcaData, domicilioFiscalArca]);
    const today = useMemo(() => getDateNow(), []);

    const getErrors = () => {
        const errors = {};

        if (formValues.denominacion.length === 0) errors.denominacion = VALIDATION_MESSAGE.REQUIRED;
        if (formValues.idFormaJuridica <= 0) errors.idFormaJuridica = VALIDATION_MESSAGE.REQUIRED_SHORT;
        if (formValues.idJurisdiccion <= 0) errors.idJurisdiccion = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (formValues.fechaConstitucion === null) errors.fechaConstitucion = VALIDATION_MESSAGE.REQUIRED_DATE;

        if (formValues.fechaConstitucion !== null && CompareDates(formValues.fechaConstitucion, today) > 0) {
            errors.fechaConstitucion = 'No puede ser posterior a hoy';
        }

        return errors;
    }

    const { errorOf, validate } = useFormErrors(getErrors);

    //handles
    const formHandleProxy = (event) => {
        formHandle(event);
        setPendingChange(true);
    }
    const openAccordion = (accordion) => {
        setState(prevState => {
            return {...prevState, accordions: {...prevState.accordions, [accordion]: true}};
        });
    }
    const handleClickGuardar = () => {
        if (formValues.idTipoDocumento <= 0 || formValues.numeroDocumento.length === 0) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Debe ingresar un Documento');
            openAccordion('documentos');
            return;
        }

        if (!validate()) {
            openAccordion('datos');
            return;
        }

        const isValidDomicilioLegal = IsValidDireccion.get("PersonaJuridicaDomicilioLegal")();
        if (!isValidDomicilioLegal.result) {
            openAccordion('domicilioLegal');
            if (isValidDomicilioLegal.message) ShowToastMessage(ALERT_TYPE.ALERT_WARNING, isValidDomicilioLegal.message);
            return;
        }

        const isValidDomicilioFiscal = IsValidDireccion.get("PersonaJuridicaDomicilioFiscal")();
        if (!isValidDomicilioFiscal.result) {
            openAccordion('domicilioFiscal');
            if (isValidDomicilioFiscal.message) ShowToastMessage(ALERT_TYPE.ALERT_WARNING, isValidDomicilioFiscal.message);
            return;
        }

        if (state.id === 0) {
            AddPersonaJuridica();
        }
        else {
            ModifyPersonaJuridica();
        }
    } 
    const handleClickSalir = () => {
        if (!pendingChange) window.close()
        else showMessageModal({
            ...MESSAGE_MODALS.CONFIRM_SALIR, onConfirm: () => {
                allowNavigation();
                window.close();
            }
        })
    }
    const handleClickVolver = () => {
        if (!pendingChange) dispatch(dataTaggerActionClear(processKey));
        navigate({ to: `/personas-juridicas` })
    }
    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
            const message = error.message;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }
    const callbackSuccessFindPersonaJuridica = (response) => {
        response.json()
        .then((data) => {
            if (data.persona.fechaConstitucion) data.persona.fechaConstitucion = new Date(data.persona.fechaConstitucion);
 
            data.mediosPago.forEach(x => {
                if (x.fechaVencimiento) x.fechaVencimiento = new Date(x.fechaVencimiento);
            });

            setState(prevState => {
                return {...prevState, loading: false, entity: data};
            });
            setPendingChange(false);
            formSet({
                idTipoDocumento: data.persona.idTipoDocumento,
                numeroDocumento: data.persona.numeroDocumento,
                denominacion: data.persona.denominacion,
                nombreFantasia: data.persona.nombreFantasia,
                idFormaJuridica: data.persona.idFormaJuridica,
                idJurisdiccion: data.persona.idJurisdiccion,
                fechaConstitucion: data.persona.fechaConstitucion,
                mesCierre: data.persona.mesCierre
            });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    };      
    
    //funciones

    
    function FindPersonaJuridica() {
        
        setState(prevState => {
        return {...prevState, loading: true};
        });

        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PERSONA_JURIDICA,
            paramsUrl,
            null,
            callbackSuccessFindPersonaJuridica,
            callbackNoSuccess,
            callbackError
        );

    }

    function AddPersonaJuridica() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((row) => {
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Persona jurídica ingresada correctamente", () => {
                    dispatch(dataTaggerActionClear(processKey));
                    allowNavigation();
                    navigate({ to: `/persona-juridica/edit/${row.persona.id}`, refresh: true, replace: true, })
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const dataBody = {
            persona: {
                ...state.entity.persona,
                idTipoDocumento: parseInt(formValues.idTipoDocumento),
                numeroDocumento: formValues.numeroDocumento,
                denominacion: formValues.denominacion,
                nombreFantasia: formValues.nombreFantasia,
                idFormaJuridica: parseInt(formValues.idFormaJuridica),
                idJurisdiccion: parseInt(formValues.idJurisdiccion),
                fechaConstitucion: formValues.fechaConstitucion,
                mesCierre: formValues.mesCierre
            },
            domicilioLegal: GetDataDireccion.get("PersonaJuridicaDomicilioLegal")(),
            domicilioFiscal: GetDataDireccion.get("PersonaJuridicaDomicilioFiscal")(),
            archivos: (dataTagger[processKey]) ? dataTagger[processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[processKey]) ? dataTagger[processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[processKey]) ? dataTagger[processKey].Etiqueta.filter(f => f.state !== 'o') : [],
            documentos: state.entity.documentos.filter(f => f.state !== 'o'),
            contactos: state.entity.contactos.filter(f => f.state !== 'o'),
            mediosPago: state.entity.mediosPago.filter(f => f.state !== 'o'),
            rubrosAfip: state.entity.rubrosAfip.filter(f => f.state !== 'o'),
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.PERSONA_JURIDICA,
            null,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    } 

    function ModifyPersonaJuridica() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Persona jurídica actualizada correctamente", () => {
                dispatch(dataTaggerActionClear(processKey));
                actualizacionArca.reiniciar();
                callbackSuccessFindPersonaJuridica(response);
                // const url = APPCONFIG.SITE.WEBAPP + 'personas-juridicas';
                // window.location.href = url;
            });
        };

        const dataBody = {
            persona: {
                ...state.entity.persona,  
                idTipoDocumento: parseInt(formValues.idTipoDocumento),
                numeroDocumento: formValues.numeroDocumento,
                denominacion: formValues.denominacion,
                nombreFantasia: formValues.nombreFantasia,
                idFormaJuridica: parseInt(formValues.idFormaJuridica),
                idJurisdiccion: parseInt(formValues.idJurisdiccion),
                fechaConstitucion: formValues.fechaConstitucion,
                mesCierre: formValues.mesCierre,
                ...actualizacionArca.marca()
            },
            domicilioLegal: GetDataDireccion.get("PersonaJuridicaDomicilioLegal")(),
            domicilioFiscal: GetDataDireccion.get("PersonaJuridicaDomicilioFiscal")(),
            archivos: (dataTagger[processKey]) ? dataTagger[processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[processKey]) ? dataTagger[processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[processKey]) ? dataTagger[processKey].Etiqueta.filter(f => f.state !== 'o') : [],
            documentos: state.entity.documentos.filter(f => f.state !== 'o'),
            contactos: state.entity.contactos.filter(f => f.state !== 'o'),
            mediosPago: state.entity.mediosPago.filter(f => f.state !== 'o'),
            rubrosAfip: state.entity.rubrosAfip.filter(f => f.state !== 'o'),
        };
        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PERSONA_JURIDICA,
            paramsUrl,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function FilasArca(persona) {
        const idFormaJuridica = mapFormaJuridica(persona.formaJuridica, getListLista('FormaJuridica'));
        const cuits = state.entity.documentos.filter(f => f.state !== 'r' && parseInt(f.idTipoDocumento) === TIPO_DOCUMENTO.CUIT).map(f => f.numeroDocumento);
        if (cuits.length === 0 && parseInt(cabecera.idTipoDocumento) === TIPO_DOCUMENTO.CUIT) cuits.push(cabecera.numeroDocumento);
        return [
            { campo: 'denominacion', etiqueta: 'Denominación', actual: formValues.denominacion, arca: persona.razonSocial },
            { campo: 'idFormaJuridica', etiqueta: 'Forma jurídica', tipo: 'lista', lista: 'FormaJuridica', actual: formValues.idFormaJuridica, arca: idFormaJuridica,
              sinDato: persona.formaJuridica ? `${persona.formaJuridica} (sin equivalente)` : undefined },
            { campo: 'fechaConstitucion', etiqueta: 'Fecha de constitución', tipo: 'fecha', actual: formValues.fechaConstitucion,
              arca: persona.fechaContratoSocial ? new Date(persona.fechaContratoSocial + 'T12:00:00') : null },
            { campo: 'mesCierre', etiqueta: 'Mes de cierre', tipo: 'mes', actual: formValues.mesCierre, arca: persona.mesCierre },
            { campo: 'cuit', etiqueta: 'CUIT', actual: cuits.join(', '), arca: persona.cuit },
            { campo: 'domicilioLegal', etiqueta: 'Domicilio legal', tipo: 'direccion', actual: GetDataDireccion.get("PersonaJuridicaDomicilioLegal")(), arca: persona.domicilioLegal },
            { campo: 'domicilioFiscal', etiqueta: 'Domicilio fiscal', tipo: 'direccion', actual: GetDataDireccion.get("PersonaJuridicaDomicilioFiscal")(), arca: persona.domicilioFiscal },
        ];
    }

    // Pasa al formulario sólo lo que el operador eligió: no guarda, no quita documentos y deja el resto como estaba. Lo
    // elegido que ya era igual (`iguales`) no se reescribe: queda lo que estaba, con sus tildes, y cuenta como completado
    function AplicarArca(campos, persona, iguales) {
        const updates = {};
        const completados = [];
        const reescribir = campo => campos.includes(campo) && !iguales.includes(campo);
        if (campos.includes('denominacion')) {
            if (reescribir('denominacion')) updates.denominacion = persona.razonSocial;
            completados.push('denominacion');
        }
        if (campos.includes('idFormaJuridica')) {
            if (reescribir('idFormaJuridica')) updates.idFormaJuridica = mapFormaJuridica(persona.formaJuridica, getListLista('FormaJuridica'));
            completados.push('idFormaJuridica');
        }
        if (campos.includes('fechaConstitucion')) {
            if (reescribir('fechaConstitucion')) updates.fechaConstitucion = new Date(persona.fechaContratoSocial + 'T12:00:00');
            completados.push('fechaConstitucion');
        }
        if (campos.includes('mesCierre')) {
            if (reescribir('mesCierre')) updates.mesCierre = persona.mesCierre;
            completados.push('mesCierre');
        }

        const documentos = documentosArca(campos, persona, state.entity.documentos,
            { idTipoPersona: TIPO_PERSONA.JURIDICA, idPersona: state.id, idInicial: sequenceValue || 1 });
        documentos.forEach(() => dispatch(sequenceActionNext()));
        const principal = documentos.find(f => f.principal);
        if (principal) {
            updates.idTipoDocumento = principal.idTipoDocumento;
            updates.numeroDocumento = principal.numeroDocumento;
        }

        const domicilios = {};
        const aplicados = {};
        const reemplazados = [];
        [['domicilioLegal', 'PersonaJuridicaDomicilioLegal'], ['domicilioFiscal', 'PersonaJuridicaDomicilioFiscal']].forEach(([campo, id]) => {
            const dom = persona[campo];
            if (!campos.includes(campo) || !dom) return;
            // igual o distinto, se bloquea sólo si ARCA lo trajo completo: si no, pierde el bloqueo que tenía, así se completa
            if (domicilioArcaCompleto(dom)) completados.push(campo);
            else reemplazados.push(campo);
            // el domicilio igual tampoco se reemplaza: no pierde entrecalles ni referencia ni se vuelve a georreferenciar
            if (!reescribir(campo)) return;
            domicilios[campo] = {
                ...GetDataDireccion.get(id)(),
                entreCalle1: '', entreCalle2: '', referencia: '', idZonaGeoreferencia: 0, latitud: 0, longitud: 0,
                ...mapArcaDomicilio(dom)
            };
            aplicados[campo] = dom;
        });

        formSet({...formValues, ...updates});
        setState(prevState => {
            let entity = CloneObject(prevState.entity);
            entity.documentos = [...prevState.entity.documentos, ...documentos];
            Object.assign(entity, domicilios);
            return {...prevState, entity: entity};
        });
        actualizacionArca.registrar(completados, aplicados, reemplazados);
        actualizacionArca.cerrar();
        setPendingChange(true);
    }

    function UpdateEntity(typeEntity, row) {
        // o:original a:add m:modify r:remove c:modify only childs

        let entity = CloneObject(state.entity);

        if (typeEntity === 'Documento') {
            if (row.state === 'a') {
                entity.documentos.push(row);
            }
            else if (row.state === 'm') {
                const index = entity.documentos.indexOf(entity.documentos.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.documentos[index] = row;
                }
            }
            else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.documentos = entity.documentos.filter(f => f.id !== row.id);
                }
                else {
                    let item = entity.documentos.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }

            const principal = entity.documentos.find(x => x.principal && x.state !== 'r');
            if (principal.idTipoDocumento !== parseInt(formValues.idTipoDocumento) || 
                principal.numeroDocumento !== formValues.numeroDocumento) {
                formSet({...formValues,
                    idTipoDocumento: principal.idTipoDocumento,
                    numeroDocumento: principal.numeroDocumento               
                });
            }

        }

        if (typeEntity === 'Contacto') {
            if (row.state === 'a') {
                entity.contactos.push(row);
            }
            else if (row.state === 'm') {
                const index = entity.contactos.indexOf(entity.contactos.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.contactos[index] = row;
                }
            }
            else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.contactos = entity.contactos.filter(f => f.id !== row.id);
                }
                else {
                    let item = entity.contactos.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        if (typeEntity === 'MedioPago') {
            if (row.state === 'a') {
                entity.mediosPago.push(row);
            }
            else if (row.state === 'm') {
                const index = entity.mediosPago.indexOf(entity.mediosPago.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.mediosPago[index] = row;
                }
            }
            else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.mediosPago = entity.mediosPago.filter(f => f.id !== row.id);
                }
                else {
                    let item = entity.mediosPago.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        if (typeEntity === 'RubroAfip') {
            if (row.state === 'a') {
                let item = entity.rubrosAfip.find(x => Math.abs(x.id) === Math.abs(row.id));
                if (isNull(item)) {
                    entity.rubrosAfip.push({ ...row, id: -row.id }); //se pone negativo para identificar los registros nuevos
                }
                else {
                    if (item.state === 'r') {
                        item.state = 'o'; //recupero el original
                    }
                    else {
                        //cualquier otro estado lo estaría metiendo duplicado (seria un error)
                    }
                }
            }
            else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.rubrosAfip = entity.rubrosAfip.filter(f => f.id !== row.id);
                }
                else {
                    let item = entity.rubrosAfip.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        setState(prevState => {
            return {...prevState, entity: entity};
        });
        setPendingChange(true);
    }


    function ToggleAccordionInfo() {
        setState(prevState => {
            return {...prevState, showInfo: !prevState.showInfo};
        });
    }
    function ToggleAccordion(accordion) {
        let accordions = CloneObject(state.accordions);
        accordions[accordion] = !accordions[accordion];
        setState(prevState => {
            return {...prevState, accordions: accordions};
        });
    }

    const accordionClose = <span className="material-symbols-outlined search-i">chevron_right</span>
    const accordionOpen = <span className="material-symbols-outlined search-i">expand_more</span>

    return (
    <>

        <Loading visible={state.loading}></Loading>

        <SectionHeading 
            titles={[
                'Personas',
                { title: 'Jurídicas', url: '/personas-juridicas' },
                params.mode === OPERATION_MODE.NEW ? 'Nueva' : `Documento: ${formValues.numeroDocumento}`
            ]} />

        {arca.showArcaLookup && (
            <ArcaLookup
                tipoPersonaEsperado="JURIDICA"
                onPersonaFound={arca.handlePersonaFound}
                onSkip={arca.handleSkip}
            />
        )}

        {!arca.showArcaLookup && (<>
        <div className="m-top-20">

            <section className='section-accordion'>

                <div className="m-top-20 m-bottom-20">

                    <div className="form-content m-left-20">
                        <div className='row'>

                            <div className={`mb-3 col-12 ${actualizacionArca.visible ? 'col-md' : 'col-md-5'}`}>
                                <label htmlFor="persona" className="form-label">Denominación</label>
                                <input
                                    name="persona"
                                    type="text"
                                    placeholder=""
                                    className="form-control"
                                    value={ `${formValues.denominacion}` }
                                    disabled={true}
                                >
                                </input>
                            </div>
                            <div className="mb-3 col-10 col-md-2">
                                <label htmlFor="idTipoDocumento" className="form-label">Tipo de documento</label>
                                <InputLista
                                    name="idTipoDocumento"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.idTipoDocumento }
                                    disabled={true}
                                    lista="TipoDocumento"
                                />
                            </div>
                            <div className="mb-3 col-10 col-md-2">
                                <label htmlFor="numeroDocumento" className="form-label">Documento</label>
                                <input
                                    name="numeroDocumento"
                                    type="text"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.numeroDocumento }
                                    disabled={true}
                                />
                            </div>
                            <div className="mb-3 col-2 col-md-1">
                                <div className="text-left m-top-32" title="Verificado">
                                    <span
                                        title={arcaOrigen ? "Verificado (ARCA)" : ""}
                                        className={arcaOrigen ? "material-symbols-outlined verified-data" : "material-symbols-outlined unverified-data"}
                                    >
                                        Verified
                                    </span>
                                </div>
                            </div>
                            {actualizacionArca.visible &&
                            <div className="mb-3 col-12 col-md-auto">
                                <button className="btn btn-outline-primary text-nowrap m-top-32" onClick={actualizacionArca.abrir}>
                                    Actualizar desde ARCA
                                </button>
                            </div>
                            }
                        </div>
                    </div>

                    {actualizacionArca.abierta &&
                        <ArcaActualizacion
                            tipoPersonaEsperado="JURIDICA"
                            identificacion={identificacionArca(state.entity.documentos, false, cabecera)}
                            filas={FilasArca}
                            onAplicar={AplicarArca}
                            onCerrar={actualizacionArca.cerrar}
                        />
                    }

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('datos')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.datos) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.datos ? 'active' : ''}>Datos</h3>
                                </div>
                            </div>
                        </div>
                    </div>

                    {(state.accordions.datos &&
                    <div className='accordion-body'>
                        <div className="form-content">
                            <div className='row'>
                                <div className='container-fluid mt-3 col-md-12'>
                                    <div className='row'>
                                        <div className="col-6 col-md-4">
                                            <FormLabel htmlFor="denominacion" required>Denominación</FormLabel>
                                            <input
                                                name="denominacion"
                                                type="text"
                                                placeholder=""
                                                className={`form-control ${errorOf('denominacion') ? 'is-invalid' : ''}`}
                                                value={ formValues.denominacion }
                                                onChange={ formHandleProxy }
                                                disabled={arca.isArcaField('denominacion') || actualizacionArca.bloqueado('denominacion')}
                                                maxLength={250}
                                            />
                                            <FormError message={errorOf('denominacion')} />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <label htmlFor="nombreFantasia" className="form-label">Nombre de fantasía</label>
                                            <input
                                                name="nombreFantasia"
                                                type="text"
                                                placeholder=""
                                                className="form-control"
                                                value={ formValues.nombreFantasia }
                                                onChange={ formHandleProxy }
                                                disabled={false}
                                                maxLength={250}
                                            />
                                            <FormError />
                                        </div>
                                        <div className="col-6 col-md-2">
                                            <FormLabel htmlFor="idFormaJuridica" required>Forma jurídica</FormLabel>
                                            <InputLista
                                                name="idFormaJuridica"
                                                placeholder=""
                                                className={`form-control ${errorOf('idFormaJuridica') ? 'is-invalid' : ''}`}
                                                value={ formValues.idFormaJuridica }
                                                onChange={ formHandleProxy }
                                                disabled={(arca.isArcaField('idFormaJuridica') && formValues.idFormaJuridica > 0) || actualizacionArca.bloqueado('idFormaJuridica')}
                                                lista="FormaJuridica"
                                            />
                                            <FormError message={errorOf('idFormaJuridica')} />
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <FormLabel htmlFor="idJurisdiccion" required>Jurisdicción</FormLabel>
                                            <InputEntidad
                                                name="idJurisdiccion"
                                                placeholder=""
                                                className={`form-control ${errorOf('idJurisdiccion') ? 'is-invalid' : ''}`}
                                                value={ formValues.idJurisdiccion }
                                                onChange={ formHandleProxy }
                                                disabled={false}
                                                entidad="Provincia"
                                                tipName={TIPS.NAME.MODALS.JURISDICCION_MODAL}
                                            />
                                            <FormError message={errorOf('idJurisdiccion')} />
                                        </div>
                                        <div className="col-6 col-md-2">
                                            <FormLabel htmlFor="fechaConstitucion" required>Fecha de constitución</FormLabel>
                                            <DatePickerCustom
                                                name="fechaConstitucion"
                                                placeholder=""
                                                className={`form-control ${errorOf('fechaConstitucion') ? 'is-invalid' : ''}`}
                                                value={ formValues.fechaConstitucion }
                                                onChange={ formHandle }
                                                disabled={arca.isArcaField('fechaConstitucion') || actualizacionArca.bloqueado('fechaConstitucion')}
                                                maxValue={today}
                                            />
                                            <FormError message={errorOf('fechaConstitucion')} />
                                        </div>
                                        <div className="col-6 col-md-2">
                                            <label htmlFor="mesCierre" className="form-label">Mes de cierre</label>
                                            <Select
                                                name="mesCierre"
                                                className="form-control"
                                                value={ formValues.mesCierre }
                                                options={GetMeses().map(item => ({ value: item.key, label: item.value }))}
                                                emptyOption="Sin selección"
                                                onChange={ formHandleProxy }
                                                disabled={arca.isArcaField('mesCierre') || actualizacionArca.bloqueado('mesCierre')}
                                            />
                                            <FormError />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('documentos')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.documentos) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.documentos ? 'active' : ''}>Documentos</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(state.accordions.documentos &&
                    <div className='accordion-body'>
                        <DocumentosGrid
                            processKey={processKey}
                            disabled={state.mode === OPERATION_MODE.VIEW}
                            data={{
                                idTipoPersona: 501, //PersonaJuridica
                                idPersona: state.id,
                                list: state.entity.documentos
                            }}
                            onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                        />
                    </div>
                    )}

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('contactos')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.contactos) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.contactos ? 'active' : ''}>Información de contacto</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(state.accordions.contactos &&
                    <div className='accordion-body'>
                        <ContactosGrid
                            processKey={processKey}
                            disabled={state.mode === OPERATION_MODE.VIEW}
                            data={{
                                entidad: 'PersonaJuridica',
                                idEntidad: state.id,
                                list: state.entity.contactos
                            }}
                            onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                        />
                    </div>
                    )}
                                        
                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('domicilioLegal')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.domicilioLegal) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.domicilioLegal ? 'active' : ''}>Domicilio legal</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={(state.accordions.domicilioLegal) ? "accordion-body" : "accordion-hide"}>
                        <div className='row form-basic'>
                            <div className="col-12">
                                <DireccionForm
                                    key={`domLegal_${arca.arcaData ? 'arca' : domicilioLegalArca ? `arca${domicilioLegalArca.vez}` : 'manual'}`}
                                    id="PersonaJuridicaDomicilioLegal"
                                    data={{
                                        entity: state.entity.domicilioLegal
                                    }}
                                    disabled={state.mode === OPERATION_MODE.VIEW || actualizacionArca.bloqueado('domicilioLegal')}
                                    autoGeocode={!!arcaAutoGeocodeLegal}
                                    autoGeocodeAddress={arcaAutoGeocodeLegal}
                                    initFormEdit={state.mode === OPERATION_MODE.NEW}
                                    setPendingChange={setPendingChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('domicilioFiscal')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.domicilioFiscal) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.domicilioFiscal ? 'active' : ''}>Domicilio fiscal</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={(state.accordions.domicilioFiscal) ? "accordion-body" : "accordion-hide"}>
                        <div className='row form-basic'>
                            <div className="col-12">
                                <DireccionForm
                                    key={`domFiscal_${arca.arcaData ? 'arca' : domicilioFiscalArca ? `arca${domicilioFiscalArca.vez}` : 'manual'}`}
                                    id="PersonaJuridicaDomicilioFiscal"
                                    data={{
                                        entity: state.entity.domicilioFiscal
                                    }}
                                    disabled={state.mode === OPERATION_MODE.VIEW || actualizacionArca.bloqueado('domicilioFiscal')}
                                    autoGeocode={!!arcaAutoGeocodeFiscal}
                                    autoGeocodeAddress={arcaAutoGeocodeFiscal}
                                    initFormEdit={state.mode === OPERATION_MODE.NEW}
                                    setPendingChange={setPendingChange}
                                />
                            </div>
                        </div>
                    </div>

                    {accessMedioPago.view &&
                    <>
                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('mediosPago')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.mediosPago) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.mediosPago ? 'active' : ''}>Medios de pago</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(state.accordions.mediosPago &&
                    <div className='accordion-body'>
                        <MediosPagoGrid
                            processKey={processKey}
                            disabled={state.mode === OPERATION_MODE.VIEW || !accessMedioPago.edit}
                            data={{
                                idTipoPersona: 501, //PersonaJuridica
                                idPersona: state.id,
                                list: state.entity.mediosPago
                            }}
                            onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                        />
                    </div>
                    )}
                    </>
                    }

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordion('rubrosAfip')}>
                                <div className='accordion-header-title'>
                                    {(state.accordions.rubrosAfip) ? accordionOpen : accordionClose}
                                    <h3 className={state.accordions.rubrosAfip ? 'active' : ''}>Rubros AFIP</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(state.accordions.rubrosAfip &&
                    <div className='accordion-body'>
                        <RubrosAfipGrid
                            disabled={state.mode === OPERATION_MODE.VIEW}
                            data={{
                                list: state.entity.rubrosAfip
                            }}
                            onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                        />
                    </div>
                    )}

                    <div className='accordion-header m-top-20'>
                        <div className='row'>
                            <div className="col-12" onClick={() => ToggleAccordionInfo()}>
                                <div className='accordion-header-title'>
                                    {(state.showInfo) ? accordionOpen : accordionClose}
                                    <h3 className={state.showInfo ? 'active' : ''}>Información adicional</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(state.showInfo &&
                    <div className='accordion-body'>
                        <DataTaggerFormRedux
                            title="Información adicional de persona jurídica"
                            processKey={processKey}
                            entidad="PersonaJuridica"
                            idEntidad={state.id}
                            disabled={state.mode === OPERATION_MODE.VIEW}
                            onChange={(row) => setPendingChange(true)}
                        />
                    </div>
                    )}

                </div>
            </section>
        </div>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                {isNoHeader ? (
                    <button className="btn back-button float-start" onClick={handleClickSalir}>Salir</button>
                ) : (
                    <button className="btn back-button float-start" onClick={handleClickVolver}>Volver</button>
                )}
                {state.mode !== OPERATION_MODE.VIEW &&
                <button
                    className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                    onClick={ (event) => handleClickGuardar() }
                    disabled={!pendingChange}
                >
                    Guardar
                </button>
                }
            </div>
        </footer>
        </>)}

    </>
    )
}

export default PersonaJuridicaView;
